import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { CitationsSection } from '@terminal/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '@terminal/components/text-section/TextSection';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import { ImpactedCompanies } from '@terminal/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '@terminal/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useTranslation } from 'react-i18next';

import { CatalystDrawerHeader } from '../catalyst-drawer-header/CatalystDrawerHeader';
import { splitSectionGroups } from '../utils';
import styles from './CompanyCatalystWidget.module.scss';

export type CompanyCatalystWidgetProps = {
  insightId: string;
  expandCitations?: boolean;
};

export const CompanyCatalystWidget = ({
  insightId,
  expandCitations = false,
}: CompanyCatalystWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const { params } = useQueryParams();
  const { trackCatalystDrawerOpen } = usePosthogEvents();
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  const utmSource = params[queryKeys.utmSource] ?? undefined;
  const entityTag = params[queryKeys.entityTag] ?? undefined;
  const isCompanyCatalystDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.catalystInsightsDrawer &&
    params[queryKeys.type] === 'company';

  useTrackEmailDrawerOpen({
    isDrawerOpen: isCompanyCatalystDrawerOpen,
    utmSource,
    trackingKey: insightId ? `${insightId}-${entityTag ?? ''}` : undefined,
    onTrackEmailOpen: () => {
      trackCatalystDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        type: 'company',
        insightId,
        entities: entityTag ? [entityTag] : undefined,
      });
    },
  });

  if (error) {
    throw error;
  }

  if (isLoading) {
    return (
      <SkeletonLoader
        className={styles.loadingSkeletonWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '64px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '136px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: '100%',
              height: '400px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!catalystInsight?.identifier || !catalystInsight) {
    return null;
  }

  const { firstSectionGroup, otherSectionGroups } = splitSectionGroups(
    catalystInsight.section_groups
  );

  return (
    <ErrorBoundaryWrapper>
      <ErrorBoundaryWrapper>
        <CatalystDrawerHeader
          identifier={catalystInsight.identifier}
          catalystInsight={catalystInsight}
          isCompanyCatalyst
        />
      </ErrorBoundaryWrapper>
      <div className={styles.sectionGroupsWrapper}>
        {firstSectionGroup.map(group => (
          <div
            className={styles.groupWrapper}
            key={group.title}
            data-testid="first-section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map(section => (
              <TextSection
                key={section.title}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            source={POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER}
            withPill
          />
        </div>
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          source={POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
          insightCreatedAt={catalystInsight.created_at}
        />
        {otherSectionGroups.map(group => (
          <div
            className={styles.groupWrapper}
            key={group.title}
            data-testid="other-section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map(section => (
              <TextSection
                key={section.title}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        {!!catalystInsight.citations.length && (
          <div className={styles.citationsSectionWrapper}>
            <div className={styles.citationsTitle}>
              {t('insights:catalyst.citations')}
            </div>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
              isExpanded={expandCitations}
            />
          </div>
        )}
      </div>
    </ErrorBoundaryWrapper>
  );
};
